<template>
  <section class="admin-sessions-content">
    <GridBlockBase id="sessions-listing" :title="t('ui.layout.nav.sessions')">
      <template #icon>
        <calendarIcon color="black" filled />
      </template>
      <template #body>
        <SessionsList :sessions="filteredSessionsRef" :isPending="isPendingRef" order="desc" />
      </template>
      <template #headerExtra>
        <div class="filters-container">
          <UsersSearch :search-callback="searchCallback" @emit:result="setEmail" :searchType="'sessions'" />
          <!-- <CustomSelect
            id="sessions-status"
            :value="selectedStatusRef"
            :options="sessionsListOptionsRef"
            @emit:change="onFilterType"
          /> -->
        </div>

        <div
          class="btn-filters"
          role="button"
          tabindex="0"
          :aria-expanded="popoverIsOpenRef"
          @click.stop="openPopoverOnClick"
          @keyup.enter.prevent="openPopoverOnKey"
        >
          <FilterIcon color="black" filled />
          {{ t('ui.button.filters') }}
        </div>

        <Popover :popover="{ name: 'sessionsFilters', isOpen: popoverIsOpenRef, event: popoverEventRef || undefined }">
          <template #content>
            <UsersSearch
              class="popover-search-bar"
              :search-callback="searchCallback"
              results-position="top"
              @emit:result="setEmail"
            />
            <!-- <CustomSelect
              id="sessions-status-mobile"
              :value="selectedStatusRef"
              :options="sessionsListOptionsRef"
              placement="top"
              @emit:change="onFilterType"
            /> -->
          </template>
        </Popover>

        <CustomButton
          class="sessions-content__export-button"
          :is-loading="adminExportSessionsActionIsloadingRef"
          icon-position="right"
          color="primary"
          usage="button"
          data-action="admin-sessions-export"
          :data-attached="JSON.stringify({ program: selectedProgramRef, periodId: selectedPeriodIdRef })"
        >
          <template #icon>
            <span class="sessions-content__export-button__text">{{ t('ui.button.export') }}</span>
            <DownloadIcon color="black" />
            <DropdownIcon color="black" />
          </template>
        </CustomButton>
      </template>
      <template #footer>
        <div>
          <span v-if="isPendingRef" class="table-sub-info">{{ t('ui.message.loading') }}</span>
          <span v-else class="table-sub-info">
            {{
              t('ui.pagination.resultsOfOn', {
                start: Math.min(Math.max(1, perPageRef * (pageRef - 1) + 1), totalSessionsRef ?? 1),
                end: Math.min(perPageRef * pageRef, totalSessionsRef ?? 1),
                total: totalSessionsRef,
              })
            }}
          </span>
        </div>
        <div class="page-select-wrapper">
          <CustomSelect
            placement="top"
            id="page-select"
            :value="`${pageRef}`"
            :options="pagesOptionsRef"
            :label="t('ui.pagination.page')"
            hide-label
            @emit:change="onPageChange"
          />
          <span v-if="isPendingRef" class="table-sub-info table-sub-info__total-pages">
            {{ t('ui.message.loading') }}
          </span>
          <span v-else class="table-sub-info table-sub-info__total-pages">
            {{ t('ui.pagination.totalPages', { total: totalPagesRef }) }}
          </span>
        </div>
        <MobilePagination :page="pageRef" :total="totalPagesRef ?? 1" @change="onPageChange" />
      </template>
    </GridBlockBase>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { AdminSessionCardType } from '@/features/admin/sessions/components/session-card/session-card.vue'
import SessionsList from '@/features/admin/sessions/components/sessions-list/sessions-list.vue'
import useProgram from '@/features/programs/hooks/use-program.hook'
import {
  selectGetSelectedPeriodId,
  selectGetSelectedProgramId,
} from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import useSessionsAdminManager from '@/features/sessions/hooks/use-sessions-admin-manager.hook'
import { SessionStatusEnumType } from '@/features/sessions/types'
import type { SessionsStatusesOptionsType } from '@/features/sessions/utils'
import { sessionAdminStatusesOptionsMap, SessionOptionEnumDefaultAllType } from '@/features/sessions/utils'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import GridBlockBase from '@/features/ui/components/grid-block-base/grid-block-base.vue'
import calendarIcon from '@/features/ui/components/icons/calendar-icon.vue'
import DownloadIcon from '@/features/ui/components/icons/download-icon.vue'
import DropdownIcon from '@/features/ui/components/icons/dropdown-icon.vue'
import FilterIcon from '@/features/ui/components/icons/filter-icon.vue'
import { selectGetMoreActions } from '@/features/ui/components/more-actions-menu/store/more-actions.selectors'
import type { AdminSessionsExportMoreActionsType } from '@/features/ui/components/more-actions-menu/store/more-actions.store'
import moreActionsStore from '@/features/ui/components/more-actions-menu/store/more-actions.store'
import MobilePagination from '@/features/ui/components/pagination/mobile-pagination.vue'
import Popover from '@/features/ui/components/popover/popover.vue'
import CustomSelect from '@/features/ui/components/select/custom-select.vue'
import UsersSearch from '@/features/ui/pages/admin/users/users-search/users-search.vue'
import type { V2UserAdminDataType } from '@/features/users/types'
import usePopoverTrigger from '@/hooks/use-popover-trigger.hook'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()

const {
  isOpenRef: popoverIsOpenRef,
  eventRef: popoverEventRef,
  openOnClick: openPopoverOnClick,
  openOnKey: openPopoverOnKey,
} = usePopoverTrigger()

const userMailToFilterRef = ref<string | null>(null)

const setEmail = (user: V2UserAdminDataType | undefined) => {
  userMailToFilterRef.value = user?.email ?? null
}

const searchCallback = () => {
  pageRef.value = 1
}

const pageRef = ref<number>(1)
const perPageRef = ref<number>(20)

const { sessionsRef, totalPagesRef, totalSessionsRef, isPendingRef } = useSessionsAdminManager({
  filterEmailSearchRef: userMailToFilterRef,
  pageRef,
  perPageRef,
})

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const selectedPeriodIdRef = selectGetSelectedPeriodId(programsStore)
const { programRef: selectedProgramRef } = useProgram(selectedProgramIdRef)

// check if the export action is loading
const adminExportSessionsMoreActionsRef = selectGetMoreActions(moreActionsStore, 'admin-sessions-export')
const adminExportSessionsActionIsloadingRef = computed(
  () =>
    (adminExportSessionsMoreActionsRef.value as AdminSessionsExportMoreActionsType)?.attachedData?.isLoading ?? false,
)

function onPageChange(value: number) {
  pageRef.value = value
}

// current selected status
const selectedStatusRef = ref<SessionsStatusesOptionsType>(SessionOptionEnumDefaultAllType.ALL)

// on select change, fitler the sessions
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function onFilterType(value: SessionsStatusesOptionsType) {
  selectedStatusRef.value = value
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sessionsListOptionsRef = computed(() => {
  return [
    {
      label: `${sessionAdminStatusesOptionsMap[SessionOptionEnumDefaultAllType.ALL]}`,
      value: SessionOptionEnumDefaultAllType.ALL,
    },
    {
      label: `${sessionAdminStatusesOptionsMap[SessionStatusEnumType.PLANNED]}`,
      value: SessionStatusEnumType.PLANNED,
    },
    {
      label: `${sessionAdminStatusesOptionsMap[SessionStatusEnumType.ONGOING]}`,
      value: SessionStatusEnumType.ONGOING,
    },
    {
      label: `${sessionAdminStatusesOptionsMap[SessionStatusEnumType.FINISHED]}`,
      value: SessionStatusEnumType.FINISHED,
    },
    {
      label: `${sessionAdminStatusesOptionsMap[SessionStatusEnumType.CANCELLED]}`,
      value: SessionStatusEnumType.CANCELLED,
    },
  ] satisfies { label: string; value: SessionsStatusesOptionsType }[]
})

const pagesOptionsRef = computed(() => {
  return Array.from({ length: totalPagesRef.value ?? 0 }, (_, i) => ({
    label: `${t('ui.pagination.page')} ${i + 1}`,
    value: `${i + 1}`,
  })) satisfies { label: string; value: string }[]
})

// function to filter the sessions
const filteredSessionsRef = computed<AdminSessionCardType[]>(() => {
  switch (selectedStatusRef.value) {
    // case SessionStatusEnumType.PLANNED:
    //   return plannedSessionsRef.value
    // case SessionStatusEnumType.FINISHED:
    //   return completedSessionsRef.value
    // case SessionStatusEnumType.CANCELLED:
    //   return canceledSessionsRef.value
    // case SessionStatusEnumType.ONGOING:
    //   return onGoingSessionsRef.value

    // default case ALL
    default:
      return [
        ...sessionsRef.value,
        // ...plannedSessionsRef.value,
        // ...completedSessionsRef.value,
        // ...canceledSessionsRef.value,
        // ...onGoingSessionsRef.value,
      ]
  }
})
</script>

<style lang="scss">
@import './sessions-content.scss';
@include mq('<desktop') {
  .table-sub-info__total-pages {
    display: none;
  }
}
</style>
